/**
 * Sistema de Push Notifications con Web Push API
 * Permite notificaciones incluso con la app cerrada
 */
import { api } from './api';

/**
 * Convierte una clave pública VAPID de base64 a Uint8Array
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Suscribe al usuario a notificaciones push
 * @returns {Promise<boolean>} true si se suscribió correctamente
 */
export async function subscribeToPushNotifications() {
  const logs = []; // Guardar logs para mostrar al final
  
  try {
    logs.push('🔔 Iniciando suscripción...');
    console.log('🔔 [PUSH] Iniciando suscripción...');
    
    // Verificar soporte
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      const msg = '❌ Tu navegador no soporta notificaciones push';
      console.warn('❌ [PUSH] Push notifications no soportadas');
      alert(msg);
      return false;
    }
    logs.push('✅ Navegador soporta push');
    console.log('✅ [PUSH] Navegador soporta push');

    // Verificar permisos de notificaciones
    if (Notification.permission !== 'granted') {
      const msg = '❌ Necesitas aceptar los permisos de notificaciones primero';
      console.warn('❌ [PUSH] No hay permiso para notificaciones');
      alert(msg);
      return false;
    }
    logs.push('✅ Permisos concedidos');
    console.log('✅ [PUSH] Permisos concedidos');

    // Obtener el Service Worker registration
    logs.push('🔄 Esperando Service Worker...');
    console.log('🔄 [PUSH] Esperando Service Worker...');
    const registration = await navigator.serviceWorker.ready;
    logs.push('✅ Service Worker listo');
    console.log('✅ [PUSH] Service Worker listo');

    // Obtener la clave pública VAPID del backend
    logs.push('🔄 Obteniendo VAPID key...');
    console.log('🔄 [PUSH] Obteniendo VAPID key del servidor...');
    const { data } = await api.get('/api/push/vapid-public-key');
    const vapidPublicKey = data.publicKey;
    logs.push('✅ VAPID key recibida');
    console.log('✅ [PUSH] VAPID key recibida:', vapidPublicKey.substring(0, 20) + '...');

    // Suscribirse a push notifications
    logs.push('🔄 Suscribiendo al navegador...');
    console.log('🔄 [PUSH] Suscribiendo al navegador...');
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
    });
    logs.push('✅ Suscripción del navegador creada');
    console.log('✅ [PUSH] Suscripción del navegador creada');

    // Convertir la suscripción a JSON (ya incluye las keys en Base64 URL-safe)
    const subscriptionJSON = subscription.toJSON();
    console.log('📦 [PUSH] Datos a enviar:', {
      endpoint: subscriptionJSON.endpoint.substring(0, 50) + '...',
      hasKeys: !!subscriptionJSON.keys,
      hasP256dh: !!subscriptionJSON.keys?.p256dh,
      hasAuth: !!subscriptionJSON.keys?.auth
    });

    // Enviar la suscripción al backend
    logs.push('🔄 Enviando al servidor...');
    console.log('🔄 [PUSH] Enviando suscripción al servidor...');
    const response = await api.post('/api/push/subscribe', {
      endpoint: subscriptionJSON.endpoint,
      keys: {
        p256dh: subscriptionJSON.keys.p256dh,
        auth: subscriptionJSON.keys.auth
      }
    });
    logs.push('✅ Servidor respondió OK');
    console.log('✅ [PUSH] Respuesta del servidor:', response.data);

    logs.push('✅ ¡ÉXITO TOTAL!');
    console.log('✅ [PUSH] ¡Suscripción completada exitosamente!');
    
    // Mostrar resumen de éxito
    alert('✅ ¡Notificaciones push activadas!\n\nAhora recibirás alertas incluso con la app cerrada.');
    return true;

  } catch (error) {
    console.error('❌ [PUSH] ERROR COMPLETO:', error);
    console.error('❌ [PUSH] Error message:', error.message);
    console.error('❌ [PUSH] Error response:', error.response?.data);
    console.error('❌ [PUSH] Error status:', error.response?.status);
    
    // Construir mensaje de error detallado
    logs.push('❌ ERROR: ' + error.message);
    if (error.response) {
      logs.push('Status: ' + error.response.status);
      if (error.response.data) {
        logs.push('Detalle: ' + JSON.stringify(error.response.data));
      }
    }
    
    // Mostrar todos los logs en un alert
    alert('❌ ERROR EN PUSH NOTIFICATIONS:\n\n' + logs.join('\n'));
    
    return false;
  }
}

/**
 * Desuscribe al usuario de notificaciones push
 */
export async function unsubscribeFromPushNotifications() {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      // Desuscribir del navegador
      await subscription.unsubscribe();

      // Notificar al backend
      await api.post('/api/push/unsubscribe', {
        endpoint: subscription.endpoint
      });

      console.log('✅ Desuscrito de push notifications');
      return true;
    }

    return false;
  } catch (error) {
    console.error('Error desuscribiendo de push notifications:', error);
    return false;
  }
}

/**
 * Verifica si el usuario ya está suscrito
 */
export async function isPushSubscribed() {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      return false;
    }

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    return subscription !== null;
  } catch (error) {
    console.error('Error verificando suscripción push:', error);
    return false;
  }
}
